import { I18n } from '@aws-amplify/core';
import { notification } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/utils/query-keys';
import { BeraReportV2Service } from '@/components/views/BeraJob/services/api/beraReportV2Service';
import {
	CreateBeraReportV2Request,
	DeleteBeraReportV2Request,
	BeraReportsV2ListRequest,
	UpdateBeraJobSummaryFileRequest,
	UpdateBeraReportSegmentsRequest,
	UpdateAllBeraReportSegmentsRequest
} from '@/components/views/BeraJob/hooks/types/request';

const beraReportV2Service = new BeraReportV2Service();

export function useBeraReportsByJobSummary(params: BeraReportsV2ListRequest) {
	const enabled = !!params.organization_id && !!params.company_id && !!params.bera_job_summary_id;

	return useQuery(
		[QUERY_KEYS.GET_BERA_REPORTS_V2, params],
		() => beraReportV2Service.listBeraReportsByJobSummary(params),
		{ enabled, keepPreviousData: true }
	);
}

export function useUpdateBeraJobSummaryFile() {
	const queryClient = useQueryClient();

	return useMutation(
		(params: UpdateBeraJobSummaryFileRequest) => beraReportV2Service.updateBeraJobSummaryFile(params),
		{
			onError: () => {
				notification.error({
					message: I18n.get('Ops... something happened!'),
					description: I18n.get('Failed to link the selected video to the report')
				});
			},
			onSuccess: () => {
				queryClient.invalidateQueries(['bera_job_summary']);
				queryClient.invalidateQueries(['bera_job_summary_list']);
				queryClient.invalidateQueries(['bera_files']);
				queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORTS_V2]);
			}
		}
	);
}

export function useCreateBeraReportV2() {
	return useMutation((body: CreateBeraReportV2Request) => beraReportV2Service.createBeraReport(body));
}

export function useUpdateBeraReportSegments() {
	return useMutation((body: UpdateBeraReportSegmentsRequest) => beraReportV2Service.updateBeraReportSegments(body));
}

export function useUpdateAllBeraReportSegments() {
	return useMutation((body: UpdateAllBeraReportSegmentsRequest) =>
		beraReportV2Service.updateAllBeraReportSegments(body)
	);
}

export function useDeleteBeraReportV2() {
	return useMutation((params: DeleteBeraReportV2Request) => beraReportV2Service.deleteBeraReport(params));
}
